// ============================================================================
// CLIENT MASTER SCREEN
// ============================================================================
// Master CRUD screen for Clients via prima-api.
//
// Follows the EmployeeMasterScreen pattern with additions:
//  - Multiple FK lookups: clientTypes, clientGroups, managers, partners, countries, states
//  - Cascading country -> state dropdown
//  - Complex formInitialData mapping

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { formatDateOnly } from 'utils';
import {
  useClients,
  useCreateClient,
  useUpdateClient,
  useDeleteClient,
} from 'hooks/api/useClient';
import { useClientTypes } from 'hooks/api/useClientType';
import { useClientGroups } from 'hooks/api/useClientGroup';
import { useEmployees } from 'hooks/api/useEmployee';
import { useCountries } from 'hooks/api/useCountry';
import { useStateProvinces } from 'hooks/api/useStateProvince';
import type {
  Client,
  ClientSearchRequest,
  CreateClientRequest,
  UpdateClientRequest,
} from 'api/types';
import ClientFormModal, {
  type ClientFormData,
} from 'components/modals/modules/client/ClientFormModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

// Column definitions for the ColumnSelector
const columnDefinitions: ColumnDefinition[] = [
  { id: 'code',            label: 'Code',           field: 'code',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'name',            label: 'Client',         field: 'name',            required: true,  defaultVisible: true,  sortable: true },
  { id: 'legalName',       label: 'Legal Name',     field: 'legalName',       required: false, defaultVisible: true,  sortable: true },
  { id: 'clientTypeName',  label: 'Client Type',    field: 'clientTypeName',  required: false, defaultVisible: true,  sortable: true },
  { id: 'groupName',       label: 'Group',          field: 'groupName',       required: false, defaultVisible: true,  sortable: true },
  { id: 'managerName',     label: 'Manager',        field: 'managerName',     required: false, defaultVisible: true,  sortable: true },
  { id: 'partnerName',     label: 'Partner',        field: 'partnerName',     required: false, defaultVisible: true,  sortable: true },
  { id: 'email',           label: 'Email',          field: 'email',           required: false, defaultVisible: true,  sortable: true },
  { id: 'phone1',          label: 'Phone',          field: 'phone1',          required: false, defaultVisible: false, sortable: false },
  { id: 'status',          label: 'Status',         field: 'statusName',      required: false, defaultVisible: true,  sortable: true },
  { id: 'createdAt',       label: 'Created',        field: 'createdAt',       required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt',       label: 'Updated',        field: 'updatedAt',       required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'client-master-columns';

interface ClientMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const ClientMasterScreen: React.FC<ClientMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --------------------------------------------------------------------------
  // Column visibility state
  // --------------------------------------------------------------------------
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // --------------------------------------------------------------------------
  // Lookup data
  // --------------------------------------------------------------------------
  const { data: clientTypes, refetch: refetchClientTypes } = useClientTypes({
    page: 1, pageSize: 100, includeInactive: false,
  });
  const { data: clientGroups, refetch: refetchClientGroups } = useClientGroups({
    page: 1, pageSize: 100, includeInactive: false,
  });
  const { data: managersRaw, refetch: refetchManagers } = useEmployees({
    page: 1, pageSize: 100, isManager: true, includeInactive: false,
  });
  const { data: partnersRaw, refetch: refetchPartners } = useEmployees({
    page: 1, pageSize: 100, isPartner: true, includeInactive: false,
  });
  const { data: countries, refetch: refetchCountries } = useCountries();

  // Cascading state for the form modal
  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const { data: states } = useStateProvinces(selectedCountryId || null);

  const clientTypeOptions = useMemo(
    () => clientTypes.map((ct) => ({ value: ct.id, label: ct.name })),
    [clientTypes]
  );
  const clientGroupOptions = useMemo(
    () => clientGroups.map((cg) => ({ value: cg.id, label: cg.name })),
    [clientGroups]
  );
  const managerOptions = useMemo(
    () => managersRaw.map((e) => ({
      value: e.id,
      label: e.isPartner
        ? `★ ${e.code} - ${e.name} (Partner)`
        : `${e.code} - ${e.name}`,
    })),
    [managersRaw]
  );
  const partnerOptions = useMemo(
    () => partnersRaw.map((e) => ({ value: e.id, label: `${e.code} - ${e.name}` })),
    [partnersRaw]
  );

  // Map managerId → partnerId for auto-filling partner when manager is selected.
  const managerPartnerMap = useMemo(
    () => Object.fromEntries(
      managersRaw.map((e) => [
        e.id,
        e.partnerId || (e.isPartner ? e.id : ''),
      ]).filter(([, pid]) => pid !== '')
    ),
    [managersRaw]
  );

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.id, label: `${c.countryCode} - ${c.countryName}` })),
    [countries]
  );
  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.id, label: `${s.provinceCode} - ${s.provinceName}` })),
    [states]
  );

  // Filter options
  const clientTypeFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...clientTypes.map((ct) => ({ value: ct.id, label: ct.name }))],
    [clientTypes]
  );
  const clientGroupFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...clientGroups.map((cg) => ({ value: cg.id, label: cg.name }))],
    [clientGroups]
  );
  const managerFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...managersRaw.map((e) => ({
      value: e.id,
      label: e.isPartner ? `★ ${e.code} - ${e.name}` : `${e.code} - ${e.name}`,
    }))],
    [managersRaw]
  );
  const partnerFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...partnersRaw.map((e) => ({ value: e.id, label: `${e.code} - ${e.name}` }))],
    [partnersRaw]
  );

  const loadLookups = useCallback(() => {
    refetchClientTypes();
    refetchClientGroups();
    refetchManagers();
    refetchPartners();
    refetchCountries();
  }, [refetchClientTypes, refetchClientGroups, refetchManagers, refetchPartners, refetchCountries]);

  // --------------------------------------------------------------------------
  // Server-side query state
  // --------------------------------------------------------------------------
  const [queryParams, setQueryParams] = useState<ClientSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
  });
  const { data, pagination, loading, refetch } = useClients(queryParams);

  // --------------------------------------------------------------------------
  // View state
  // --------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // Filter inputs
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [legalNameFilter, setLegalNameFilter] = useState('');
  const [clientTypeFilter, setClientTypeFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [managerFilter, setManagerFilter] = useState<string>('');
  const [partnerFilter, setPartnerFilter] = useState<string>('');
  const [emailFilter, setEmailFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // --------------------------------------------------------------------------
  // Modal state
  // --------------------------------------------------------------------------
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Client | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      loadLookups();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.code === recordCode || r.name === recordCode);
        if (rec) {
          setSelectedRecord(rec);
          if (rec.countryId) setSelectedCountryId(rec.countryId);
        }
      }
      setFormMode(restoredMode);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [data, loadLookups]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mutation hooks
  const { createClient } = useCreateClient();
  const { updateClient } = useUpdateClient();
  const { deleteClient } = useDeleteClient();

  // --------------------------------------------------------------------------
  // Column definitions for SyutGrid
  // --------------------------------------------------------------------------
  const tableColumns: SyutGridColumn<Client>[] = useMemo(
    () => [
      { id: 'code', header: 'Code', accessorKey: 'code', size: 120, enableSorting: true },
      {
        id: 'name',
        header: 'Client',
        accessorKey: 'name',
        size: 200,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            title={row.original.name}
            style={{
              maxWidth: '180px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.original.name}
          </div>
        ),
      },
      { id: 'legalName', header: 'Legal Name', accessorKey: 'legalName', size: 180, enableSorting: true,
        cell: ({ row }) => row.original.legalName || '-',
      },
      { id: 'clientTypeName', header: 'Client Type', accessorKey: 'clientTypeName', size: 140, enableSorting: true,
        cell: ({ row }) => row.original.clientTypeName || '-',
      },
      { id: 'groupName', header: 'Group', accessorKey: 'groupName', size: 130, enableSorting: true,
        cell: ({ row }) => row.original.groupName || '-',
      },
      { id: 'managerName', header: 'Manager', accessorKey: 'managerName', size: 160, enableSorting: true,
        cell: ({ row }) => row.original.managerName || '-',
      },
      { id: 'partnerName', header: 'Partner', accessorKey: 'partnerName', size: 160, enableSorting: true,
        cell: ({ row }) => row.original.partnerName || '-',
      },
      { id: 'email', header: 'Email', accessorKey: 'email', size: 180, enableSorting: true,
        cell: ({ row }) => row.original.email || '-',
      },
      { id: 'phone1', header: 'Phone', accessorKey: 'phone1', size: 120, enableSorting: false,
        cell: ({ row }) => row.original.phone1 || '-',
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<Client>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  // --------------------------------------------------------------------------
  // Handlers -- open modals
  // --------------------------------------------------------------------------
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRecord(null);
    setSelectedCountryId('');
    setFormMode('create');
    setFormApiError(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    (record: Client) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.code}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(record);
      setSelectedCountryId(record.countryId || '');
      setFormMode('edit');
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleDeleteClick = useCallback((record: Client) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Client | null>(null);

  const handleView = useCallback((record: Client) => {
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // Save handler
  // --------------------------------------------------------------------------
  const handleSaveForm = useCallback(
    async (formData: ClientFormData) => {
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        if (formMode === 'create') {
          const payload: CreateClientRequest = {
            code: formData.code,
            name: formData.name,
            legalName: formData.legalName || undefined,
            aliasName: formData.aliasName || undefined,
            dateOfIncorporation: formData.dateOfIncorporation || undefined,
            clientTypeId: formData.clientTypeId,
            groupId: formData.groupId || undefined,
            managerId: formData.managerId,
            partnerId: formData.partnerId || undefined,
            address: formData.address || undefined,
            countryId: formData.countryId || undefined,
            countryName: formData.countryName || undefined,
            stateId: formData.stateId || undefined,
            stateName: formData.stateName || undefined,
            cityName: formData.cityName || undefined,
            postalCode: formData.postalCode || undefined,
            phone1: formData.phone1 || undefined,
            phone2: formData.phone2 || undefined,
            email: formData.email || undefined,
            website: formData.website || undefined,
            statusCode: formData.statusCode,
          };
          await createClient(payload);
          notificationService.toast.success(`Client "${payload.name}" created.`);
        } else if (selectedRecord) {
          const payload: UpdateClientRequest = {
            code: formData.code,
            name: formData.name,
            legalName: formData.legalName || undefined,
            aliasName: formData.aliasName || undefined,
            dateOfIncorporation: formData.dateOfIncorporation || undefined,
            clientTypeId: formData.clientTypeId,
            groupId: formData.groupId || undefined,
            managerId: formData.managerId,
            partnerId: formData.partnerId || undefined,
            address: formData.address || undefined,
            countryId: formData.countryId || undefined,
            countryName: formData.countryName || undefined,
            stateId: formData.stateId || undefined,
            stateName: formData.stateName || undefined,
            cityName: formData.cityName || undefined,
            postalCode: formData.postalCode || undefined,
            phone1: formData.phone1 || undefined,
            phone2: formData.phone2 || undefined,
            email: formData.email || undefined,
            website: formData.website || undefined,
            statusCode: formData.statusCode,
            rowVersion: selectedRecord.rowVersion,
          };
          await updateClient(selectedRecord.id, payload);
          notificationService.toast.success(`Client "${payload.name}" updated.`);
        }
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
        loadLookups();
      } catch (err: any) {
        setIsSavingForm(false);
        const apiBody = err?.response?.data;
        let message = 'Failed to save client. Please try again.';
        if (typeof apiBody?.message === 'string' && apiBody.message) {
          message = apiBody.message;
        } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
          message = apiBody.errors.join(', ');
        } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
          message = Object.values(apiBody.errors).flat().join(', ');
        } else if (err?.message) {
          message = err.message;
        }
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [formMode, selectedRecord, createClient, updateClient, refetch, loadLookups]
  );

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteClient(deleteTarget.id);
      notificationService.toast.success(`Client "${deleteTarget.name}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete client.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteClient, refetch]);

  // --------------------------------------------------------------------------
  // Filter Apply / Clear
  // --------------------------------------------------------------------------
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.code) count++;
    if (queryParams.name) count++;
    if (queryParams.clientTypeId) count++;
    if (queryParams.groupId) count++;
    if (queryParams.managerId) count++;
    if (queryParams.partnerId) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.code, queryParams.name, queryParams.clientTypeId, queryParams.groupId, queryParams.managerId, queryParams.partnerId, queryParams.statusCode]);

  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      code: codeFilter || undefined,
      name: nameFilter || undefined,
      clientTypeId: clientTypeFilter || undefined,
      groupId: groupFilter || undefined,
      managerId: managerFilter || undefined,
      partnerId: partnerFilter || undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [codeFilter, nameFilter, clientTypeFilter, groupFilter, managerFilter, partnerFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setLegalNameFilter('');
    setClientTypeFilter('');
    setGroupFilter('');
    setManagerFilter('');
    setPartnerFilter('');
    setEmailFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // --------------------------------------------------------------------------
  // Row actions
  // --------------------------------------------------------------------------
  const getRowActions = useCallback(
    (row: Client): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [
        {
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        },
      ];
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick]
  );

  // Action buttons
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Add New Client',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch]
  );

  // Form initial data (edit mode)
  const formInitialData: ClientFormData | null = useMemo(() => {
    if (formMode !== 'edit' || !selectedRecord) return null;

    return {
      code: selectedRecord.code,
      name: selectedRecord.name,
      legalName: selectedRecord.legalName || '',
      aliasName: selectedRecord.aliasName || '',
      dateOfIncorporation: selectedRecord.dateOfIncorporation ? selectedRecord.dateOfIncorporation.substring(0, 10) : '',
      clientTypeId: selectedRecord.clientTypeId || '',
      groupId: selectedRecord.groupId || '',
      managerId: selectedRecord.managerId || '',
      partnerId: selectedRecord.partnerId || '',
      address: selectedRecord.address || '',
      countryId: selectedRecord.countryId || '',
      countryName: selectedRecord.countryName || '',
      stateId: selectedRecord.stateId || '',
      stateName: selectedRecord.stateName || '',
      cityName: selectedRecord.cityName || '',
      postalCode: selectedRecord.postalCode || '',
      phone1: selectedRecord.phone1 || '',
      phone2: selectedRecord.phone2 || '',
      email: selectedRecord.email || '',
      website: selectedRecord.website || '',
      statusCode: (selectedRecord.statusCode as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
    };
  }, [formMode, selectedRecord]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Client Master"
        description="Manage clients, assignments, and contact details"
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.file size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, email...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* List view */}
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={
                  loading ? 'Loading clients...' : 'No clients found'
                }
              />
            )}

            {/* Grid (card) view */}
            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div
                      style={{
                        gridColumn: '1 / -1',
                        textAlign: 'center',
                        padding: '2rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {loading ? 'Loading clients...' : 'No clients found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: 'var(--text-secondary)',
                                marginBottom: '0.25rem',
                              }}
                            >
                              {item.code}
                            </div>
                            <h3
                              title={item.name}
                              style={{
                                fontSize: '1rem',
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                                margin: 0,
                                marginBottom: '0.25rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                              }}
                            >
                              {item.name}
                            </h3>
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                              {item.clientTypeName || '-'}
                            </div>
                          </div>
                          <ActionMenu items={getRowActions(item)} />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 'auto', flexWrap: 'wrap' }}>
                          {item.managerName && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              {item.managerName}
                            </span>
                          )}
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter panel */}
        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input
                type="text"
                className="form-input"
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Client Name</label>
              <input
                type="text"
                className="form-input"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Legal Name</label>
              <input
                type="text"
                className="form-input"
                value={legalNameFilter}
                onChange={(e) => setLegalNameFilter(e.target.value)}
                placeholder="Filter by legal name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Client Type"
                options={clientTypeFilterOptions}
                value={clientTypeFilter}
                onChange={(val) => setClientTypeFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Group"
                options={clientGroupFilterOptions}
                value={groupFilter}
                onChange={(val) => setGroupFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Manager"
                options={managerFilterOptions}
                value={managerFilter}
                onChange={(val) => setManagerFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Partner"
                options={partnerFilterOptions}
                value={partnerFilter}
                onChange={(val) => setPartnerFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Email</label>
              <input
                type="text"
                className="form-input"
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                placeholder="Filter by email..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(val) => setStatusFilter((val as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      {/* Form modal */}
      <ClientFormModal
        isOpen={formModalOpen}
        mode={formMode}
        initialData={formInitialData}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        clientTypeOptions={clientTypeOptions}
        clientGroupOptions={clientGroupOptions}
        managerOptions={managerOptions}
        partnerOptions={partnerOptions}
        managerPartnerMap={managerPartnerMap}
        countryOptions={countryOptions}
        stateOptions={stateOptions}
        onCountryChange={(countryId) => setSelectedCountryId(countryId)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Client"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}" (${deleteTarget.code})? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {/* View Modal — read-only client details */}
      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-lg">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Client — {viewRecord.code}</h2>
              <button className="modal-close" onClick={() => { setViewModalOpen(false); setViewRecord(null); }} title="Close">
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2">
                <div>
                  <label className="form-label">Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.code || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Name</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.name || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Legal Name</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.legalName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Alias Name</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.aliasName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Date of Incorporation</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{formatDateOnly(viewRecord.dateOfIncorporation)}</div>
                </div>
                <div>
                  <label className="form-label">Client Type</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.clientTypeName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Group</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.groupName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Manager</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.managerName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Partner</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.partnerName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Phone 1</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.phone1 || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Phone 2</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.phone2 || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.email || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Website</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.website || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Address</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.address || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Country</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.countryName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">State</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.stateName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">City</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.cityName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Postal Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.postalCode || '—'}</div>
                </div>
                {/*
                 * Statutory registrations (PAN/GSTIN/CIN/BURS-VAT/etc.) are
                 * shown on the Client Registration screen (one client may
                 * carry multiple registrations across types/jurisdictions).
                 */}
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge badge-${viewRecord.statusCode === 'ACTIVE' ? 'active' : 'inactive'}`}>{viewRecord.statusName}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModalOpen(false); setViewRecord(null); }}>Close</button>
              <button className="btn btn-primary" onClick={() => { setViewModalOpen(false); setViewRecord(null); handleEdit(viewRecord); }}>Edit</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientMasterScreen;
